import { useQuery } from '@tanstack/react-query';
import { AlertTriangleIcon, LockIcon } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTooltip,
  toast,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { usersControllerListMembersV1 } from '@/api/generated/users/users';
import type { OffboardingTaskDto } from '@/api/generated/model';
import { StatusIcon, useTaskStatusesQuery } from '@/modules/tasks';
import { todayIso } from '@/shared/lib/formSchema';
import { formatDate } from '@/shared/lib/format';
import { useUpdateOffboardingTaskMutation } from '../hooks/useOffboarding';

/** Sentinel for "no assignee" — a Radix select item cannot carry an empty value. */
const UNASSIGNED = '__unassigned__';

export interface OffboardingChecklistProps {
  offboardingId: string;
  tasks: OffboardingTaskDto[];
  /** A closed offboarding is a record, not a worklist. */
  readOnly?: boolean;
}

/**
 * The four offboarding steps, in the same row layout as onboarding's checklist.
 *
 * A locked row is read-only here, but that is presentation only — the API
 * rejects the same change with a 409, which is what actually enforces the
 * sequence. The `onError` below surfaces that message rather than swallowing
 * it, because if the two ever disagree the server is right and the user should
 * be told why.
 */
export function OffboardingChecklist({
  offboardingId,
  tasks,
  readOnly = false,
}: OffboardingChecklistProps) {
  const { t } = useTranslation();
  const updateTask = useUpdateOffboardingTaskMutation(offboardingId);

  // Members of the agency, for the assignee picker. Cached hard — the roster
  // changes far less often than the checklist is edited.
  const statuses = useTaskStatusesQuery();
  const members = useQuery({
    queryKey: ['members', 'assignable'] as const,
    queryFn: ({ signal }) => usersControllerListMembersV1({ limit: 100 }, { signal }),
    staleTime: 5 * 60_000,
  });

  const patch = (id: string, dto: Parameters<typeof updateTask.mutate>[0]['dto']) => {
    updateTask.mutate(
      { id, dto },
      {
        onError: (error: unknown) => {
          toast.error(
            error instanceof Error ? error.message : t('offboarding.checklist.updateFailed'),
          );
        },
      },
    );
  };

  return (
    <Card className={cn('gap-0 pb-0 transition-opacity', updateTask.isPending && 'opacity-70')}>
      {/* Not a numbered step: the checklist is the overview OF the four steps,
          not a fifth one. It carries their numbering in its row order. */}
      <CardHeader className="border-b pb-3">
        <CardTitle>{t('offboarding.checklist.title')}</CardTitle>
      </CardHeader>
      <div className="divide-y divide-border/70">
        {tasks.map((task) => {
          const overdue =
            task.dueDate !== null &&
            task.status.category !== 'completed' &&
            task.dueDate.slice(0, 10) < todayIso();
          // Locked steps cannot move forward, but CAN be reopened, which the
          // API allows deliberately. Disabling the control outright would make
          // an incorrectly-completed step impossible to undo from the UI.
          const statusLocked =
            readOnly || (task.locked && task.status.category !== 'completed');

          return (
            <div
              key={task.id}
              className="flex flex-col gap-3 px-4 py-3.5 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="grid min-w-0 gap-0.5">
                <div className="flex items-center gap-1.5">
                  {task.locked ? (
                    <SimpleTooltip content={task.lockedReason}>
                      <LockIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    </SimpleTooltip>
                  ) : null}
                  <span
                    className={cn('text-sm font-medium', task.locked && 'text-muted-foreground')}
                  >
                    {task.title}
                  </span>
                </div>
                <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                  <span>
                    {t('offboarding.checklist.assignedTo', {
                      name: task.assigneeName ?? t('offboarding.checklist.unassigned'),
                    })}
                  </span>
                  <span aria-hidden>·</span>
                  <span className="tabular-nums">
                    {task.dueDate
                      ? t('offboarding.checklist.due', { date: formatDate(task.dueDate) })
                      : t('offboarding.checklist.noDue')}
                  </span>
                  {overdue ? (
                    <span className="inline-flex items-center gap-1 text-destructive">
                      <AlertTriangleIcon className="size-3" />
                      {t('offboarding.checklist.overdue')}
                    </span>
                  ) : null}
                  {task.locked && task.lockedReason ? (
                    <>
                      <span aria-hidden>·</span>
                      <span>{task.lockedReason}</span>
                    </>
                  ) : null}
                </p>
                {task.description ? (
                  <p className="text-xs text-muted-foreground/80">{task.description}</p>
                ) : null}
              </div>

              <div className="grid shrink-0 grid-cols-1 gap-2 sm:grid-cols-3 lg:flex">
                <Select
                  value={task.assigneeId ?? UNASSIGNED}
                  onValueChange={(assigneeId) =>
                    patch(task.id, { assigneeIds: assigneeId === UNASSIGNED ? [] : [assigneeId] })
                  }
                  disabled={readOnly || members.isPending}
                >
                  <SelectTrigger
                    className="lg:w-40"
                    aria-label={t('offboarding.checklist.assignee')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>
                      {t('offboarding.checklist.unassigned')}
                    </SelectItem>
                    {(members.data?.data ?? []).map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name || member.email}
                      </SelectItem>
                    ))}
                    {task.assigneeId &&
                    !(members.data?.data ?? []).some((member) => member.id === task.assigneeId) ? (
                      <SelectItem value={task.assigneeId}>
                        {task.assigneeName ?? task.assigneeId}
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>

                <Input
                  type="date"
                  className="lg:w-40"
                  aria-label={t('offboarding.checklist.dueDate')}
                  disabled={readOnly}
                  value={task.dueDate?.slice(0, 10) ?? ''}
                  onChange={(event) => patch(task.id, { dueDate: event.target.value || null })}
                />

                <Select
                  value={task.status.id}
                  onValueChange={(statusId) => patch(task.id, { statusId })}
                  disabled={statusLocked}
                >
                  <SelectTrigger className="lg:w-40" aria-label={t('offboarding.checklist.status')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(statuses.data ?? [task.status]).map((status) => (
                      <SelectItem key={status.id} value={status.id}>
                        <StatusIcon category={status.category} color={status.color} />
                        {status.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
